import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ExpandMore,
  ContentCopy,
  ChevronRight,
} from "@mui/icons-material";
import React, { useState } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { get } from "lodash";

interface SchemaProperty {
  type?: string | string[];
  description?: string;
  title?: string;
  items?: SchemaProperty;
  properties?: Record<string, SchemaProperty>;
  allOf?: SchemaProperty[];
  enum?: string[];
  required?: string[] | boolean;
  $ref?: string;
  oneOf?: SchemaProperty[];
  examples?: any[];
  default?: any;
  $defs?: Record<string, SchemaProperty>;
  [key: string]: any;
}

interface SchemaDocumentationProps {
  schema: SchemaProperty;
}

// Helper to resolve $ref
const resolveRef = (root: SchemaProperty, ref: string): SchemaProperty => {
  if (!ref.startsWith("#/")) return {}; // Only support local refs for now
  const path = ref.replace("#/", "").replace(/\//g, ".");
  return get(root, path, {}) as SchemaProperty;
};

const collectProperties = (
  schema: SchemaProperty,
  rootSchema: SchemaProperty
): { props: Record<string, SchemaProperty>; required: string[] } => {
  const props: Record<string, SchemaProperty> = {};
  const required: string[] = [];

  if (Array.isArray(schema.allOf)) {
    schema.allOf.forEach((entry) => {
      const resolved = entry.$ref ? resolveRef(rootSchema, entry.$ref) : entry;
      Object.assign(props, resolved.properties ?? {});
      if (Array.isArray(resolved.required)) {
        required.push(...resolved.required);
      }
    });
  }

  Object.assign(props, schema.properties ?? {});
  if (Array.isArray(schema.required)) {
    required.push(...schema.required);
  }

  return { props, required };
};

const PropertyType = ({ type }: { type?: string | string[] }) => {
  if (!type) return null;
  const typeLabel = Array.isArray(type) ? type.join(" | ") : type;
  return (
    <Chip
      label={typeLabel}
      size="small"
      variant="outlined"
      sx={{
        height: 20,
        fontSize: "0.75rem",
        backgroundColor: "rgba(0, 0, 0, 0.04)",
        fontFamily: "monospace",
        border: "none",
      }}
    />
  );
};

const CopyButton = ({ text }: { text: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <CopyToClipboard text={text} onCopy={handleCopy}>
      <Tooltip title={copied ? "Copied!" : "Copy example"}>
        <IconButton size="small" sx={{ ml: 1 }}>
          <ContentCopy fontSize="small" />
        </IconButton>
      </Tooltip>
    </CopyToClipboard>
  );
};

const ExampleBlock = ({ examples }: { examples: any[] }) => {
  const [showMore, setShowMore] = useState(false);

  if (!examples || examples.length === 0) return null;

  const fullText = examples.map(ex => 
    typeof ex === 'string' ? ex : JSON.stringify(ex, null, 2)
  ).join('\n\n');

  const lines = fullText.split('\n');
  const isLong = lines.length > 3;
  
  const displayedText = !showMore && isLong 
    ? lines.slice(0, 3).join('\n') 
    : fullText;

  return (
    <Box sx={{ mt: 1, mb: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Example{examples.length > 1 ? 's' : ''}:</Typography>
        <Box sx={{ 
            position: 'relative', 
            bgcolor: '#f8f9fa', 
            p: 1, 
            borderRadius: 1, 
            mt: 0.5,
            overflow: 'hidden',
            border: '1px solid #eee'
        }}>
            <Box sx={{ position: 'absolute', top: 4, right: 4, zIndex: 10 }}>
                <CopyButton text={fullText} />
            </Box>
            <pre style={{ margin: 0, fontSize: '0.75rem', fontFamily: 'monospace' }}>
                {displayedText}
            </pre>
            
            {!showMore && isLong && (
                <Box sx={{ 
                    position: 'absolute', 
                    bottom: 0, 
                    left: 0, 
                    right: 0, 
                    height: 60, 
                    background: 'linear-gradient(to bottom, transparent, #f8f9fa)',
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    pb: 1
                }}>
                   <Button 
                     size="small" 
                     variant="text" 
                     onClick={() => setShowMore(true)}
                     sx={{ 
                         fontSize: '0.7rem', 
                         textTransform: 'none',
                         bgcolor: 'rgba(255,255,255,0.8)',
                         '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
                     }}
                   >
                       Show more
                   </Button>
                </Box>
            )}
             {showMore && (
                 <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                     <Button 
                         size="small" 
                         variant="text" 
                         onClick={() => setShowMore(false)}
                         sx={{ fontSize: '0.7rem', textTransform: 'none' }}
                     >
                         Show less
                     </Button>
                 </Box>
             )}
        </Box>
    </Box>
  );
}

const SchemaNode = ({
  name,
  property,
  required,
  rootSchema,
  level = 0,
  defaultExpanded = false,
}: {
  name?: string;
  property: SchemaProperty;
  required?: boolean;
  rootSchema: SchemaProperty;
  level?: number;
  defaultExpanded?: boolean;
}) => {
  // Resolve ref if present
  let schema = property;
  if (property.$ref) {
    const resolved = resolveRef(rootSchema, property.$ref);
    // Merge resolved schema with property (property takes precedence for description/title if overridden)
    schema = { ...resolved, ...property, $ref: undefined };
  }

  const { props: allProps, required: allRequired } = collectProperties(
    schema,
    rootSchema
  );
  const isObject = schema.type === "object" && Object.keys(allProps).length > 0;
  const isArray = schema.type === "array" && !!schema.items;
  const isOneOf = !!schema.oneOf;
  
  // Check if array items are simple
  let isSimpleArray = false;
  if (isArray && schema.items) {
      let itemSchema = schema.items;
      if (itemSchema.$ref) {
         itemSchema = resolveRef(rootSchema, itemSchema.$ref);
      }
      // It's simple if it's not an object with properties and not an array and not oneOf
      const itemIsObject = itemSchema.type === "object" && !!itemSchema.properties;
      const itemIsArray = itemSchema.type === "array";
      const itemIsOneOf = !!itemSchema.oneOf;
      isSimpleArray = !itemIsObject && !itemIsArray && !itemIsOneOf;
  }

  const hasChildren = isObject || (isArray && !isSimpleArray) || isOneOf;

  // Determine if we should show expand/collapse
  const [expanded, setExpanded] = useState(defaultExpanded);

  const toggleExpand = () => setExpanded(!expanded);

  const renderChildren = () => {
    if (isObject) {
      return (
        <Box sx={{ mt: 1 }}>
          {Object.entries(allProps).map(([key, subProp]) => (
            <SchemaNode
              key={key}
              name={key}
              property={subProp}
              required={allRequired.includes(key)}
              rootSchema={rootSchema}
              level={level + 1}
            />
          ))}
        </Box>
      );
    }

    if (isArray && schema.items && !isSimpleArray) {
      return (
        <Box sx={{ mt: 1 }}>
          <SchemaNode
            property={schema.items}
            rootSchema={rootSchema}
            level={level + 1}
            defaultExpanded={false} 
          />
        </Box>
      );
    }

    if (isOneOf && schema.oneOf) {
       return (
        <Box sx={{ mt: 1 }}>
          {schema.oneOf.map((subProp, idx) => (
            <Box key={idx} sx={{ mb: 2, pl: 1, borderLeft: '2px solid #eee' }}>
               <SchemaNode
                property={subProp}
                rootSchema={rootSchema}
                level={level + 1}
                defaultExpanded={true} 
              />
            </Box>
          ))}
        </Box>
      );
    }
    return null;
  };

  return (
    <Box sx={{ mb: 2, ml: level > 0 ? 2 : 0 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ mb: 0.5 }}>
            {name && (
              <Typography
                variant="subtitle2"
                component="span"
                sx={{ fontWeight: "bold", fontFamily: "monospace" }}
              >
                {name}
              </Typography>
            )}
            {required && (
              <Typography variant="caption" color="error" sx={{ fontWeight: "bold" }}>
                *required
              </Typography>
            )}
            <PropertyType type={schema.type} />
            
            {schema.title && (
                 <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary" }}>
                    - {schema.title}
                 </Typography>
            )}
          </Stack>

          {schema.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {schema.description}
            </Typography>
          )}

          {schema.default !== undefined && (
             <Typography variant="caption" display="block" sx={{ color: "text.secondary", mb: 0.5 }}>
                Default: <code>{JSON.stringify(schema.default)}</code>
             </Typography>
          )}

          {schema.enum && (
            <Box sx={{ mb: 0.5 }}>
              <Typography variant="caption" display="inline" sx={{ mr: 1 }}>
                Allowed values:
              </Typography>
               {schema.enum.map((val) => (
                 <Chip key={val} label={val} size="small" sx={{ height: 18, fontSize: '0.7rem', mr: 0.5, mb: 0.5 }} />
               ))}
            </Box>
          )}
          
          {/* Array Simple Type Note */}
          {isSimpleArray && schema.items && (
             <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                 Each element should be <b>{(schema.items.type as string) || 'string'}</b>.
             </Typography>
          )}

          <ExampleBlock examples={schema.examples || []} />

          {/* Expandable Section Header */}
          {hasChildren && (
            <Stack 
                direction="row" 
                alignItems="center" 
                onClick={toggleExpand}
                sx={{ 
                    cursor: 'pointer', 
                    mt: 0.5,
                    color: 'text.secondary',
                    '&:hover': { color: 'text.primary' }
                }}
            >
                 <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                    {expanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
                 </Box>
                 <Typography variant="body2">
                    {isObject ? "Object can have the following fields:" : 
                     isOneOf ? "One of the following:" : 
                     "Each element of array is:"}
                 </Typography>
            </Stack>
          )}

          <Collapse in={expanded}>
             {renderChildren()}
          </Collapse>
        </Box>
    </Box>
  );
};

export const SchemaDocumentation: React.FC<SchemaDocumentationProps> = ({
  schema,
}) => {
  return (
    <Box sx={{ height: "100%", overflowY: "auto", p: 2, bgcolor: "#fff" }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        {schema.title || "Configuration Documentation"}
      </Typography>
      <Typography variant="body1" paragraph>
          {schema.description}
      </Typography>
      
      {/* Render properties at root level */}
      {schema.properties && (
         <Box>
             {Object.entries(schema.properties).map(([key, prop]) => (
                 <SchemaNode 
                    key={key}
                    name={key}
                    property={prop}
                    rootSchema={schema}
                    required={Array.isArray(schema.required) ? schema.required.includes(key) : false}
                    level={0}
                    defaultExpanded={false}
                 />
             ))}
         </Box>
      )}
    </Box>
  );
};
